import { motion, type Easing } from "framer-motion";
import { Helmet } from "react-helmet-async";
import {
  FaWordpressSimple,
  FaShoppingCart,
  FaCogs,
  FaRocket,
  FaShieldAlt,
  FaLightbulb,
  FaMobileAlt,
  FaPalette, // For design/customization
  FaHandshake, // For support/partnership
  FaSearchDollar, // For SEO/ROI
  FaQuestionCircle, // For FAQ
} from "react-icons/fa"; // Added more icons for new sections
import { Link } from "react-router-dom";

// Animation variants (rest of your existing animation variants)
const fadeInLeft = {
  hidden: { opacity: 0, x: -80 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.8 } },
};

const fadeInRight = {
  hidden: { opacity: 0, x: 80 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.8 } },
};

const fadeInUp = {
  hidden: { opacity: 0, y: 50 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
};

const fadeProcessStep = (index: number) => ({
  hidden: { opacity: 0, x: index % 2 === 0 ? -50 : 50 },
  visible: {
    opacity: 1,
    x: 0,
    transition: {
      duration: 0.7,
      ease: [0, 0, 0.58, 1] as Easing,
    },
  },
});


const WordPressServices = () => {
  return (
    <>
      <Helmet>
        <title>Expert WordPress Development Services | Nadezhda Venture</title>
        <meta
          name="description"
          content="Elevate your website with custom WordPress development services. From eCommerce to speed optimization, Nadezhda Venture delivers high-performing, secure, and SEO-optimized WordPress solutions."
        />
        <meta
          name="keywords"
          content="WordPress development, WooCommerce, WordPress customization, ecommerce website, WordPress SEO, web development Noida, Nadezhda Venture"
        />
        <meta name="author" content="Nadezhda Venture" />

        {/* Open Graph for social sharing */}
        <meta property="og:title" content="Expert WordPress Development Services | Nadezhda Venture" />
        <meta property="og:description" content="Custom WordPress websites, WooCommerce stores, speed and SEO optimization by Nadezhda Venture." />
        <meta property="og:image" content="https://www.digivendtechnologies.com/public/frontend/images/web-development/wordpress-web-development.png" />
        <meta property="og:url" content="https://yourdomain.com/wordpress-development" />
        <meta property="og:type" content="website" />
      </Helmet>

      {/* SECTION 1 - Hero Section with Transparent Image and Text Overlay */}
      <section className="relative overflow-hidden flex items-center justify-center h-[400px]"> {/* Increased min-h for better visual */}
        {/* Transparent Background Image */}
        <div className="absolute inset-0 z-0 bg-black">
          <img
            src="https://cdn.pixabay.com/photo/2015/09/04/23/28/wordpress-923188_1280.jpg" // Replace with your actual transparent image URL
            alt="WordPress Transparent Background"
            className="w-full h-full object-cover opacity-95" // opacity can be adjusted
          />
          {/* Optional: Add a subtle overlay for better text readability */}
          <div className="absolute inset-0 bg-black opacity-70"></div> {/* Adjust opacity as needed */}
        </div>

        {/* Text Content */}
        <div className="max-w-4xl mx-auto text-center px-6 py-12 relative z-10"> {/* z-10 makes text appear on top */}
          <motion.h1
            className="text-3xl md:text-5xl font-extrabold mb-6 leading-tight text-white" // Text color changed back to white for visibility over the image/overlay
            initial={{ opacity: 0, y: -50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            Crafting Digital Excellence with <span className="text-orange-500">WordPress</span>
          </motion.h1>

          <motion.p
            className="mb-8 text-gray-200 leading-relaxed text-xl" // Text color changed back to gray-200
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
          >
            We build robust, beautiful, and high-performing WordPress websites that drive growth and deliver exceptional user experiences. Your vision, powered by our expertise.
          </motion.p>

          <motion.button
            // className="bg-orange-600 hover:bg-orange-700 text-white px-10 py-5 rounded-full font-semibold shadow-2xl transition-colors duration-300 text-xl"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.6 }}
          >
            <Link
    to="/contact"
    className="inline-block bg-blue-700 hover:bg-blue-500 text-white font-semibold py-3 px-6 rounded-md shadow-md hover:shadow-lg transition-all duration-300"
  >
    Let's Build Your WordPress Success
  </Link>
            
          </motion.button>
        </div>
      </section>

      {/* SECTION 2 - Why Choose Us for WordPress? */}
      <section className="bg-white text-blue-950 py-20 px-6">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold mb-12 text-center">
            Why Our WordPress Solutions <span className="text-orange-500">Stand Out</span>
          </h2>
          <div className="grid md:grid-cols-3 gap-10 text-center">
            {[
              {
                icon: <FaRocket size={50} className="text-blue-600 mb-4 mx-auto" />,
                title: "Blazing Fast Performance",
                description: "Our WordPress sites are meticulously optimized for speed, ensuring rapid load times and an excellent user experience.",
              },
              {
                icon: <FaShieldAlt size={50} className="text-green-600 mb-4 mx-auto" />,
                title: "Fortified Security",
                description: "We implement robust security measures to protect your WordPress website from threats, keeping your data safe.",
              },
              {
                icon: <FaLightbulb size={50} className="text-purple-600 mb-4 mx-auto" />,
                title: "Intuitive & User-Friendly",
                description: "Built for ease of use, your WordPress site will be simple to manage, even for non-technical users, thanks to intuitive admin panels.",
              },
              {
                icon: <FaCogs size={50} className="text-gray-700 mb-4 mx-auto" />,
                title: "Scalable & Future-Proof",
                description: "Designed to grow with your business, our WordPress solutions are highly scalable, accommodating future expansions and new features.",
              },
              {
                icon: <FaWordpressSimple size={50} className="text-blue-500 mb-4 mx-auto" />,
                title: "SEO-Optimized from Ground Up",
                description: "Our development process integrates SEO best practices, giving your website a strong foundation for higher search engine rankings.",
              },
              {
                icon: <FaMobileAlt size={50} className="text-red-500 mb-4 mx-auto" />,
                title: "Seamless Mobile Experience",
                description: "Every WordPress site we build is fully responsive, delivering a flawless and engaging experience across all devices.",
              },
            ].map((feature, i) => (
              <motion.div
                key={i}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeInUp}
                transition={{ delay: i * 0.15 }}
                className="bg-gray-50 p-8 rounded-xl shadow-lg flex flex-col items-center hover:shadow-xl transform hover:-translate-y-2 transition-all duration-300"
              >
                {feature.icon}
                <h3 className="text-2xl font-semibold mb-3 mt-2 text-blue-700">{feature.title}</h3>
                <p className="text-gray-700 leading-relaxed">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 3 - Our WordPress Development Process */}
      <section className="bg-gray-200 text-white py-20 px-6">
        <div className="max-w-6xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-12 text-blue-700">
            Our Streamlined WordPress <span className="text-orange-400">Development Journey</span>
          </h2>
          <div className="space-y-16">
            {[
              {
                step: "01",
                title: "Discovery & Strategy",
                description: "We begin by understanding your unique business objectives, target audience, and competitive landscape to define a clear WordPress strategy.",
                image: "https://img.freepik.com/free-photo/group-diverse-people-having-business-meeting_53876-25060.jpg?size=626&ext=jpg&ga=GA1.1.18264147.1710129600&semt=ais",
              },
              {
                step: "02",
                title: "Design & UX/UI Prototyping",
                description: "Crafting visually appealing and intuitive designs. We focus on user experience (UX) and user interface (UI) to create engaging WordPress mockups and wireframes.",
                image: "https://www.scalahosting.com/blog/wp-content/uploads/2022/08/Hosting-for-WordPress.png.webp",
              },
              {
                step: "03",
                title: "Development & Customization",
                description: "Bringing your WordPress vision to life. This involves custom theme development, plugin integration, custom code, and robust database setup for optimal performance.",
                image: "https://images.businessnewsdaily.com/app/uploads/2022/04/04074408/Software_Developer_Getty_RossHelen.jpg",
              },
              {
                step: "04",
                title: "Content Migration & SEO Setup",
                description: "Seamlessly migrating your existing content and implementing on-page SEO best practices to ensure your new WordPress site ranks well from day one.",
                image: "https://www.pagetraffic.in/wp-content/uploads/2022/12/site-migration-process-1024x683.jpg",
              },
              {
                step: "05",
                title: "Testing, Launch & Training",
                description: "Thorough testing across all devices and browsers, followed by a smooth launch. We also provide comprehensive training for you to manage your new WordPress site.",
                image: "https://img.freepik.com/free-photo/business-concept-with-tablet-magnifying-glass_23-2148767035.jpg?size=626&ext=jpg&ga=GA1.1.18264147.1710129600&semt=ais",
              },
              {
                step: "06",
                title: "Ongoing Maintenance & Support",
                description: "Post-launch, we offer continuous maintenance, security updates, performance monitoring, and dedicated support to keep your WordPress site running perfectly.",
                image: "https://www.nextwebi.com/assets/images/services/Services-img/Maintainance-&-Support.png",
              },
            ].map((item, i) => (
              <motion.div
                key={i}
                className={`flex flex-col md:flex-row items-center gap-8 md:gap-12 p-8 rounded-lg shadow-xl ${
                  i % 2 === 0 ? "bg-blue-700" : "bg-blue-600"
                }`}
                initial="hidden"
                whileInView="visible"
                variants={fadeProcessStep(i)}
                viewport={{ once: true, amount: 0.1 }}
              >
                <div className={`md:w-1/2 ${i % 2 === 0 ? "md:order-1" : "md:order-2"}`}>
                  <img src={item.image} alt={item.title} className="rounded-lg shadow-2xl w-full h-auto object-cover max-h-[300px]" />
                </div>
                <div className={`md:w-1/2 text-left ${i % 2 === 0 ? "md:order-2" : "md:order-1"}`}>
                  <div className="text-orange-400 text-sm font-bold mb-3 uppercase">STEP {item.step}</div>
                  <h3 className="text-2xl md:text-3xl font-bold mb-4 leading-tight">{item.title}</h3>
                  <p className="text-blue-100 leading-relaxed text-lg">{item.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 4 - Ecommerce Web Development in WordPress (WooCommerce Focus) */}
      <section className="bg-gradient-to-br from-orange-50 via-white to-orange-100 py-20 px-6 text-blue-950">
        <div className="max-w-7xl mx-auto flex flex-col-reverse md:flex-row items-center gap-12">
          {/* Left Text */}
          <motion.div
            className="flex-1 text-center md:text-left"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInLeft}
          >
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-blue-800 mb-6 leading-tight">
                Powerful <span className="text-orange-600">Ecommerce Solutions</span> with WordPress & WooCommerce
              </h2>
              <p className="text-gray-800 text-lg leading-relaxed mb-6">
                Transform your products into profits with our expert WooCommerce development services.
                We specialize in crafting highly functional, secure, and visually stunning online stores
                that provide a seamless shopping experience for your customers. From product catalogs
                to secure payment gateways and inventory management, we cover it all.
              </p>
              <p className="text-gray-800 text-lg leading-relaxed mb-8">
                Our team ensures your e-commerce platform is optimized for conversions, mobile responsiveness,
                and search engine visibility, helping you reach a wider audience and boost sales.
                Let us build a scalable and efficient online store that perfectly represents your brand.
              </p>
              <Link
  to="/contact"
  className="inline-block bg-blue-700 hover:bg-blue-800 text-white px-8 py-4 rounded-2xl font-semibold shadow-lg transition-colors duration-300 text-lg"
>
  Build My Online Store →
</Link>
            </div>
          </motion.div>

          {/* Right Image */}
          <motion.div
            className="flex-1 relative flex justify-center items-center"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInRight}
          >
            <div className="p-6 md:p-12 w-full md:w-[90%] relative z-0">
              <img
                src="https://itflexsolutions.com/wp-content/uploads/2023/10/wordpress-web-development.webp"
                alt="Ecommerce Website Showcase"
                className="relative z-10 w-full h-auto mx-auto rounded-xl shadow-2xl border-4 border-orange-300"
              />
               <div className="absolute -bottom-5 -left-5 w-24 h-24 bg-orange-400 rounded-full opacity-60 mix-blend-multiply animate-bounce-slow" />
               <div className="absolute top-0 right-0 w-32 h-32 bg-blue-400 rounded-full opacity-60 mix-blend-multiply animate-spin-slow" />
            </div>
          </motion.div>
        </div>
      </section>

      {/* SECTION 5 - What We Offer (Services List) */}
      <section className="bg-gray-100 text-blue-800 py-20 px-6">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold mb-12 text-center">
            Our Comprehensive <span className="text-orange-600">WordPress Services</span>
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                icon: <FaWordpressSimple size={30} className="text-orange-500" />,
                title: "Custom WordPress Development",
                description: "Building unique, tailor-made WordPress websites from scratch to perfectly match your brand and requirements.",
              },
              {
                icon: <FaShoppingCart size={30} className="text-blue-500" />,
                title: "WooCommerce Development",
                description: "Setting up and customizing powerful online stores with WooCommerce, complete with payment gateways and inventory management.",
              },
              {
                icon: <FaCogs size={30} className="text-green-500" />,
                title: "WordPress Theme & Plugin Customization",
                description: "Modifying existing themes and plugins or developing custom ones to extend the functionality of your website.",
              },
              {
                icon: <FaRocket size={30} className="text-purple-500" />,
                title: "WordPress Speed Optimization",
                description: "Enhancing your website's performance for faster loading times and improved user experience.",
              },
              {
                icon: <FaShieldAlt size={30} className="text-red-500" />,
                title: "WordPress Security & Maintenance",
                description: "Ensuring your WordPress site is secure, updated, and running smoothly with regular backups and maintenance.",
              },
              {
                icon: <FaLightbulb size={30} className="text-yellow-500" />,
                title: "WordPress Migration Services",
                description: "Seamlessly migrating your website to WordPress or from one hosting provider to another without data loss.",
              },
            ].map((service, i) => (
              <motion.div
                key={i}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeInUp}
                transition={{ delay: i * 0.1 }}
                className="bg-white p-6 rounded-lg shadow-md flex items-start space-x-4 hover:shadow-xl transition-shadow duration-300"
              >
                <div className="flex-shrink-0">{service.icon}</div>
                <div>
                  <h3 className="text-xl font-semibold mb-2">{service.title}</h3>
                  <p className="text-gray-700 text-base">{service.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 6 - Why Choose WordPress? (New Section) */}
      <section className="bg-gray-200 text-white py-20 px-6">
        <div className="max-w-6xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-12 text-blue-800">
            Why WordPress Powers <span className="text-orange-600">Millions of Websites</span>
          </h2>
          <div className="grid md:grid-cols-3 gap-10">
            {[
              {
                icon: <FaHandshake size={50} className="text-green-400 mb-4 mx-auto" />,
                title: "Unmatched Flexibility",
                description: "WordPress offers unparalleled flexibility, allowing for custom designs and functionalities to meet any business need.",
              },
              {
                icon: <FaSearchDollar size={50} className="text-yellow-400 mb-4 mx-auto" />,
                title: "SEO-Friendly Foundation",
                description: "Built with search engines in mind, WordPress provides a strong SEO base, making it easier to rank higher and attract organic traffic.",
              },
              {
                icon: <FaPalette size={50} className="text-pink-400 mb-4 mx-auto" />,
                title: "Vast Ecosystem & Community",
                description: "Access to thousands of themes and plugins, backed by a massive global community for support and innovation.",
              },
            ].map((reason, i) => (
              <motion.div
                key={i}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeInUp}
                transition={{ delay: i * 0.15 }}
                className="bg-blue-950 p-8 rounded-xl shadow-lg flex flex-col items-center hover:shadow-xl transform hover:-translate-y-2 transition-all duration-300"
              >
                {reason.icon}
                <h3 className="text-xl font-semibold mb-3 mt-2">{reason.title}</h3>
                <p className="text-blue-100 leading-relaxed">{reason.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 7 - Client Testimonials / Success Stories */}
      <section className="bg-gray-50 text-blue-950 py-20 px-6">
        <div className="max-w-6xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-12">
            What Our Clients Say About Our <span className="text-orange-500">WordPress Expertise</span>
          </h2>
          <div className="grid md:grid-cols-2 gap-10">
            {[
              {
                quote: "Nadezhda Venture transformed our outdated website into a modern, lightning-fast WordPress site. Their attention to detail and SEO expertise significantly boosted our online visibility. Highly recommended!",
                author: "Sarah J.",
                company: "E-Commerce Business Owner",
              },
              {
                quote: "The team at Nadezhda Venture delivered an exceptional WooCommerce store that exceeded our expectations. The process was smooth, and the result is a beautiful, highly functional platform.",
                author: "David L.",
                company: "Retail Brand Manager",
              },
            ].map((testimonial, i) => (
              <motion.div
                key={i}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeInUp}
                transition={{ delay: i * 0.2 }}
                className="bg-white p-8 rounded-lg shadow-xl text-left border-l-4 border-orange-500 hover:shadow-2xl transition-shadow duration-300"
              >
                <p className="text-lg italic mb-6 text-gray-700">"{testimonial.quote}"</p>
                <p className="font-semibold text-blue-600">{testimonial.author}</p>
                <p className="text-gray-500 text-sm">{testimonial.company}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 8 - Frequently Asked Questions (FAQ) - New Section */}
      <section className="bg-white text-blue-600 py-20 px-6">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold mb-12 text-center">
            Frequently Asked <span className="text-orange-400">Questions</span>
          </h2>
          <div className="space-y-6">
            {[
              {
                question: "What is the typical timeline for a WordPress project?",
                answer: "The timeline varies based on complexity, features, and content. A basic brochure site might take 3-4 weeks, while a complex e-commerce store could take 8-12 weeks or more. We provide a detailed timeline after discovery.",
              },
              {
                question: "Do you offer post-launch support and maintenance?",
                answer: "Yes, we offer comprehensive post-launch support, including regular updates, security monitoring, performance optimization, and bug fixes to ensure your WordPress site runs smoothly.",
              },
              {
                question: "Can you migrate my existing website to WordPress?",
                answer: "Absolutely! We specialize in seamless website migrations to WordPress, ensuring all your content, SEO rankings, and functionalities are preserved during the transition.",
              },
              {
                question: "Is WordPress good for SEO?",
                answer: "Yes, WordPress is inherently SEO-friendly, and with our expertise in SEO best practices, we ensure your website is optimized for search engines to achieve higher rankings and better visibility.",
              },
            ].map((faq, i) => (
              <motion.div
                key={i}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.1 }}
                variants={fadeInUp}
                transition={{ delay: i * 0.1 }}
                className="bg-gray-200 p-6 rounded-lg shadow-md"
              >
                <details className="group">
                  <summary className="flex justify-between items-center cursor-pointer text-lg font-semibold text-black">
                    {faq.question}
                    <FaQuestionCircle className="text-orange-400 group-open:rotate-180 transition-transform duration-300" />
                  </summary>
                  <p className="mt-4 text-blue-900 leading-relaxed">{faq.answer}</p>
                </details>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

     
    </>
  );
};

export default WordPressServices;